import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Filter,
  Play,
  X,
  MapPin,
  LogIn,
  RefreshCw,
  ChevronDown,
  User,
  Loader,
} from "lucide-react";

// Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Country Place IDs for Data-driven styling
// const COUNTRY_PLACE_IDS = {
//   ChIJCzYy5IS16lQRQrfeQ5K5Oxw: "United States",
//   "ChIJqZHHQhE7WgIReiWIMkOg-MQ": "United Kingdom",
//   ChIJAVkDPzdOgEcRMDhiYfhvkS4: "France",
//   ChIJLxl_1w9OZzQRRFJmfNR1QvU: "Japan",
//   ChIJ38WHZwf9KysRUhNblaFnglM: "Australia",
//   "ChIJa76xwh5ymkcRW-WRjmtd6HU": "Germany",
// };

// Authentication Hook
const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [athleteId, setAthleteId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      // Check URL params for OAuth callback first
      const urlParams = new URLSearchParams(window.location.search);
      const authResult = urlParams.get("auth");
      const athleteIdParam = urlParams.get("athlete_id");
      const authError = urlParams.get("error");

      if (authResult === "success" && athleteIdParam) {
        // OAuth callback successful
        setAthleteId(athleteIdParam);
        setIsAuthenticated(true);
        localStorage.setItem("strava_athlete_id", athleteIdParam);
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        setLoading(false);
        return;
      }

      if (authError) {
        // OAuth error
        console.error("Auth error:", authError);
        localStorage.removeItem("strava_athlete_id");
        setLoading(false);
        return;
      }

      // Check for stored athlete ID
      const storedAthleteId = localStorage.getItem("strava_athlete_id");
      if (storedAthleteId) {
        // Verify with backend that auth is still valid
        try {
          const response = await fetch(
            `${API_BASE_URL}/auth/status/${storedAthleteId}`
          );

          if (response.ok) {
            const authData = await response.json();
            if (authData.authenticated) {
              setAthleteId(storedAthleteId);
              setIsAuthenticated(true);
              console.log("✅ Found valid stored authentication");
            } else {
              // Auth invalid, clear storage
              localStorage.removeItem("strava_athlete_id");
              console.log("❌ Stored authentication invalid");
            }
          } else {
            // Backend error or auth not found
            localStorage.removeItem("strava_athlete_id");
            console.log("❌ Backend authentication check failed");
          }
        } catch (error) {
          console.error("Error checking backend auth:", error);
          localStorage.removeItem("strava_athlete_id");
        }
      }
    } catch (error) {
      console.error("Error in auth check:", error);
    } finally {
      setLoading(false);
    }
  };

  const login = () => {
    window.location.href = `${API_BASE_URL}/auth/strava`;
  };

  const logout = async () => {
    if (athleteId) {
      // Optional: Call backend logout endpoint if you add one
      // await fetch(`${API_BASE_URL}/auth/logout/${athleteId}`, { method: 'POST' });
    }

    localStorage.removeItem("strava_athlete_id");
    setAthleteId(null);
    setIsAuthenticated(false);
  };

  return { isAuthenticated, athleteId, loading, login, logout };
};

// Activities Hook
const useActivities = (athleteId, activityFilter, raceFilter) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadActivities = useCallback(
    async (forceSync = false, fullSync = false) => {
      if (!athleteId) {
        console.log("No athlete ID, skipping activity load");
        return;
      }

      setLoading(true);
      setError(null);

      try {
        if (forceSync) {
          console.log(`Starting ${fullSync ? "full" : "incremental"} sync...`);

          // Start sync in background
          const syncResponse = await fetch(`${API_BASE_URL}/activities/sync`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              athlete_id: athleteId,
              full_sync: fullSync,
            }),
          });

          if (!syncResponse.ok) {
            throw new Error(`Sync failed: ${syncResponse.status}`);
          }

          const syncData = await syncResponse.json();
          console.log(
            `✅ ${fullSync ? "Full" : "Incremental"} sync started:`,
            syncData.message
          );

          // Show a notification about the sync
          if (fullSync) {
            // You could add a toast notification here
            console.log(
              "🔄 Full sync started - this will take several minutes and update countries with accurate geocoding"
            );
          }

          // Load current activities immediately (sync happens in background)
          await loadActivities(false);

          return;
        }

        // Load activities from DynamoDB (same as before)
        const params = new URLSearchParams({
          athlete_id: athleteId,
          activity_type: activityFilter,
          race_filter: raceFilter,
        });

        const response = await fetch(`${API_BASE_URL}/activities?${params}`);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        setActivities(data.activities || []);
        console.log(
          `✅ Loaded ${data.activities?.length || 0} activities from DynamoDB`
        );
      } catch (err) {
        setError(err.message);
        console.error("Activities error:", err);
      } finally {
        setLoading(false);
      }
    },
    [athleteId, activityFilter, raceFilter]
  );

  useEffect(() => {
    // Only load activities if we have an authenticated athlete
    if (athleteId) {
      loadActivities();
    } else {
      // Clear activities if not authenticated
      setActivities([]);
    }
  }, [loadActivities, athleteId]);

  return { activities, loading, error, loadActivities, setError };
};

// Google Maps Hook
const useGoogleMaps = (mapRef) => {
  const [map, setMap] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [markerClusterer, setMarkerClusterer] = useState(null);

  useEffect(() => {
    const initializeGoogleMaps = () => {
      if (window.google) {
        initMap();
        return;
      }

      const existingScript = document.querySelector(
        'script[src*="maps.googleapis.com"]'
      );
      if (existingScript) return;

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=geometry,places&loading=async`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        // Load MarkerClusterer library
        loadMarkerClusterer().then(() => {
          setTimeout(initMap, 100);
        });
      };
      document.head.appendChild(script);
    };

    const loadMarkerClusterer = () => {
      return new Promise((resolve) => {
        if (window.markerClusterer) {
          resolve();
          return;
        }

        const script = document.createElement("script");
        script.src =
          "https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js";
        script.onload = () => {
          console.log("MarkerClusterer loaded successfully");
          resolve();
        };
        script.onerror = () => {
          console.warn(
            "MarkerClusterer failed to load, continuing without clustering"
          );
          resolve();
        };
        document.head.appendChild(script);
      });
    };

    const initMap = () => {
      if (!window.google || !mapRef.current) return;

      try {
        const mapConfig = {
          zoom: 2,
          center: { lat: 20, lng: 0 },
          gestureHandling: "greedy",
          disableDefaultUI: false,
          zoomControl: true,
          mapTypeControl: false,
          scaleControl: false,
          streetViewControl: false,
          rotateControl: false,
          fullscreenControl: true,
          mapId: "10540a3a49718d2260c7e3ba",
        };

        const mapInstance = new window.google.maps.Map(
          mapRef.current,
          mapConfig
        );

        // Initialize marker clusterer with better settings for dense areas
        try {
          if (window.markerClusterer?.MarkerClusterer) {
            const clusterer = new window.markerClusterer.MarkerClusterer({
              map: mapInstance,
              markers: [],
              algorithm: new window.markerClusterer.SuperClusterAlgorithm({
                radius: 80, // Increased radius for better clustering
                maxZoom: 12, // Cluster until zoom level 12
                minZoom: 3,
                minPoints: 3, // Minimum points to form a cluster
              }),
              renderer: {
                render: ({ count, position }) => {
                  // Custom cluster styling
                  const color =
                    count > 100
                      ? "#ff4444"
                      : count > 50
                      ? "#ff8800"
                      : count > 10
                      ? "#ffaa00"
                      : "#4CAF50";
                  const size = Math.min(60, Math.max(30, Math.log(count) * 8));

                  return new window.google.maps.Marker({
                    position,
                    icon: {
                      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
                      <circle cx="${size / 2}" cy="${size / 2}" r="${
                        size / 2 - 2
                      }" fill="${color}" stroke="white" stroke-width="2"/>
                      <text x="${size / 2}" y="${
                        size / 2
                      }" text-anchor="middle" dy="0.3em" fill="white" font-size="${Math.max(
                        10,
                        size / 4
                      )}" font-weight="bold">${count}</text>
                    </svg>
                  `)}`,
                      scaledSize: new window.google.maps.Size(size, size),
                      anchor: new window.google.maps.Point(size / 2, size / 2),
                    },
                    label: {
                      text: count.toString(),
                      color: "white",
                      fontSize: "12px",
                      fontWeight: "bold",
                    },
                  });
                },
              },
            });
            setMarkerClusterer(clusterer);
            console.log("MarkerClusterer initialized successfully");
          }
        } catch (clusterError) {
          console.warn("Failed to initialize MarkerClusterer:", clusterError);
        }

        setMap(mapInstance);
        setIsLoaded(true);
      } catch (error) {
        console.error("Error initializing map:", error);
      }
    };

    initializeGoogleMaps();
  }, [mapRef]);

  return { map, isLoaded, markerClusterer };
};

// Country Place ID lookup hook
const useCountryPlaceIds = (activities) => {
  const [countryPlaceIds, setCountryPlaceIds] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const lookupCountryPlaceIds = useCallback(async () => {
    if (!window.google?.maps?.places || activities.length === 0) {
      console.log("Google Places not available or no activities");
      return;
    }

    setIsLoading(true);
    const uniqueCountries = [
      ...new Set(activities.map((a) => a.country)),
    ].filter((c) => c && c !== "Unknown" && c !== "Other");

    console.log("Looking up Place IDs for countries:", uniqueCountries);

    const newPlaceIds = {};

    // Create a map element for the Places service
    const mapDiv = document.createElement("div");
    const tempMap = new window.google.maps.Map(mapDiv, {
      zoom: 1,
      center: { lat: 0, lng: 0 },
    });
    const placesService = new window.google.maps.places.PlacesService(tempMap);

    // Process countries one by one
    for (const country of uniqueCountries) {
      try {
        console.log(`Looking up: ${country}`);
        const placeId = await findCountryPlaceId(placesService, country);
        if (placeId) {
          newPlaceIds[placeId] = country;
          console.log(`✅ Found Place ID for ${country}: ${placeId}`);
        } else {
          console.warn(`❌ Could not find Place ID for: ${country}`);
        }

        // Add delay to avoid rate limiting
        await new Promise((resolve) => setTimeout(resolve, 200));
      } catch (error) {
        console.error(`Error looking up ${country}:`, error);
      }
    }

    console.log("Final Place IDs mapping:", newPlaceIds);
    setCountryPlaceIds(newPlaceIds);
    setIsLoading(false);
  }, [activities]);

  const findCountryPlaceId = (placesService, countryName) => {
    return new Promise((resolve) => {
      const request = {
        query: countryName,
        fields: ["place_id", "name", "types"],
      };

      placesService.textSearch(request, (results, status) => {
        console.log(`Places API response for ${countryName}:`, status, results);

        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          results &&
          results.length > 0
        ) {
          // Look for a result that has 'country' in its types
          const countryResult = results.find(
            (result) => result.types && result.types.includes("country")
          );

          if (countryResult) {
            console.log(
              `Found country result for ${countryName}:`,
              countryResult
            );
            resolve(countryResult.place_id);
          } else {
            // Fallback: try the first result
            console.log(`Using first result for ${countryName}:`, results[0]);
            resolve(results[0]?.place_id || null);
          }
        } else {
          console.warn(`Places API search failed for ${countryName}:`, status);
          resolve(null);
        }
      });
    });
  };

  useEffect(() => {
    // Add a delay to ensure Google Maps is fully loaded
    if (activities.length > 0) {
      const timer = setTimeout(() => {
        if (window.google?.maps?.places) {
          console.log("Starting Place ID lookup...");
          lookupCountryPlaceIds();
        } else {
          console.log("Google Places API not ready yet");
        }
      }, 2000); // 2 second delay

      return () => clearTimeout(timer);
    }
  }, [activities.length]); // Only depend on activities length to avoid repeated calls

  return { countryPlaceIds, isLoading: isLoading };
};

// Main Component
const StravaWorldMap = () => {
  // Hooks
  const {
    isAuthenticated,
    athleteId,
    loading: authLoading,
    login,
    logout,
  } = useAuth();
  const [activityFilter, setActivityFilter] = useState("all");
  const [raceFilter, setRaceFilter] = useState("all");
  const {
    activities,
    loading: activitiesLoading,
    error,
    loadActivities,
    setError,
  } = useActivities(athleteId, activityFilter, raceFilter);

  const { countryPlaceIds, isLoading: placeLookupLoading } =
    useCountryPlaceIds(activities);

  // Map state
  const mapRef = useRef(null);
  const { map, isLoaded: mapLoaded, markerClusterer } = useGoogleMaps(mapRef);
  const markersRef = useRef([]);
  const activityPolylineRef = useRef(null);
  const countryLayerRef = useRef(null);
  const [mapZoom, setMapZoom] = useState(2);

  // UI state
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showingActivityRoute, setShowingActivityRoute] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showSyncOptions, setShowSyncOptions] = useState(false);

  // Performance state
  const [shouldShowMarkers, setShouldShowMarkers] = useState(true);
  const [lastFilterTime, setLastFilterTime] = useState(0);

  useEffect(() => {
    if (activities.length > 0) {
      const allCountries = activities.map((a) => a.country);
      const uniqueCountries = [...new Set(allCountries)];
      console.log("All unique countries in activities:", uniqueCountries);

      // Show count for each country
      const countryCounts = {};
      allCountries.forEach((country) => {
        countryCounts[country] = (countryCounts[country] || 0) + 1;
      });
      console.log("Country counts:", countryCounts);
    }
  }, [activities]);

  // Map Effects with Performance Optimizations
  useEffect(() => {
    if (map) {
      // Listen for zoom changes
      const zoomListener = map.addListener("zoom_changed", () => {
        const newZoom = map.getZoom();
        console.log("Zoom changed to:", newZoom);
        setMapZoom(newZoom);

        const shouldShow = newZoom >= 3;
        if (shouldShow !== shouldShowMarkers) {
          console.log("Changing shouldShowMarkers to:", shouldShow);
          setShouldShowMarkers(shouldShow);
        }
      });

      // Add listener for when user pans/drags the map
      const boundsListener = map.addListener("bounds_changed", () => {
        // Debounce bounds changes to avoid too many updates while dragging
        if (shouldShowMarkers) {
          clearTimeout(window.boundsTimeout);
          window.boundsTimeout = setTimeout(() => {
            console.log("Map bounds changed, updating markers");
            updateActivityMarkers();
          }, 500);
        }
      });

      return () => {
        window.google?.maps.event.removeListener(zoomListener);
        window.google?.maps.event.removeListener(boundsListener);
        clearTimeout(window.boundsTimeout);
      };
    }
  }, [map, shouldShowMarkers]);

  useEffect(() => {
    if (map && activities.length > 0) {
      updateCountryHighlighting();
    }
  }, [map, activities, countryPlaceIds]);

  // Force initial marker update when map is ready
  useEffect(() => {
    if (map && activities.length > 0) {
      console.log("Map is ready, forcing initial marker update");
      setShouldShowMarkers(true);
      setTimeout(() => {
        updateActivityMarkers();
      }, 500);
    }
  }, [map, activities.length]);

  // Debounced marker updates for better performance
  useEffect(() => {
    console.log("Marker update effect triggered", {
      hasMap: !!map,
      shouldShowMarkers,
      activitiesLength: activities.length,
      activityFilter,
      raceFilter,
      mapZoom,
    });

    if (map && shouldShowMarkers && activities.length > 0) {
      console.log("About to update markers");
      updateActivityMarkers();
    } else if (map && !shouldShowMarkers) {
      console.log("Clearing markers due to zoom");
      clearAllMarkers();
    }
  }, [map, activities, activityFilter, raceFilter, shouldShowMarkers, mapZoom]);

  // Helper Functions
  const getFilteredActivities = useCallback(() => {
    return activities.filter((activity) => {
      const typeMatch =
        activityFilter === "all" || activity.type === activityFilter;
      const raceMatch =
        raceFilter === "all" ||
        (raceFilter === "race" && activity.isRace) ||
        (raceFilter === "non-race" && !activity.isRace);
      return typeMatch && raceMatch;
    });
  }, [activities, activityFilter, raceFilter]);

  const getUnlockedCountries = useCallback(() => {
    return [...new Set(activities.map((a) => a.country))].filter(
      (c) => c && c !== "Unknown" && c !== "Other"
    );
  }, [activities]);

  const getActivityIcon = (type) => {
    const icons = { run: "🏃", ride: "🚴", swim: "🏊" };
    return icons[type] || "📍";
  };

  const getActivityColor = (type) => {
    const colors = { run: "#FF4444", ride: "#44FF44", swim: "#4444FF" };
    return colors[type] || "#FF4444";
  };

  // Map Functions
  const updateCountryHighlighting = () => {
    if (!map || !window.google) return;

    console.log("updateCountryHighlighting called");
    console.log(
      "Available countryPlaceIds:",
      Object.keys(countryPlaceIds).length
    );
    console.log("Country Place IDs:", countryPlaceIds);

    // Clear existing highlighting
    if (countryLayerRef.current) {
      countryLayerRef.current.style = null;
    }

    try {
      if (window.google.maps.FeatureType && map.getFeatureLayer) {
        const countryLayer = map.getFeatureLayer(
          window.google.maps.FeatureType.COUNTRY
        );
        countryLayerRef.current = countryLayer;

        const unlockedCountries = getUnlockedCountries();
        console.log("Unlocked countries from activities:", unlockedCountries);

        countryLayer.style = (styleOptions) => {
          const feature = styleOptions.feature;
          const countryName = countryPlaceIds[feature.placeId]; // Use dynamic Place IDs
          const hasActivities =
            countryName && unlockedCountries.includes(countryName);

          if (countryName) {
            console.log(
              `Styling ${countryName} (${feature.placeId}): hasActivities=${hasActivities}`
            );
          }

          if (hasActivities) {
            return {
              strokeColor: "#4CAF50",
              strokeOpacity: 0.8,
              strokeWeight: 2,
              fillColor: "#4CAF50",
              fillOpacity: 0.2,
            };
          } else {
            // Make sure we're applying dark styling to countries without activities
            return {
              strokeColor: "transparent",
              strokeOpacity: 0,
              strokeWeight: 0,
              fillColor: "#000000",
              fillOpacity: 0.7, // Increased opacity to make it more visible
            };
          }
        };

        countryLayer.addListener("click", (event) => {
          const countryName = countryPlaceIds[event.feature.placeId]; // Use dynamic Place IDs
          if (countryName) {
            const count = activities.filter(
              (a) => a.country === countryName
            ).length;
            console.log(`${countryName}: ${count} activities`);
          }
        });

        console.log(
          `Applied highlighting for ${
            Object.keys(countryPlaceIds).length
          } countries`
        );
      }
    } catch (error) {
      console.warn("Data-driven styling not available:", error);
    }
  };

  const updateActivityMarkers = () => {
    if (!map || !window.google || !shouldShowMarkers) return;

    console.log("Updating markers...");
    const startTime = performance.now();

    // Clear existing markers
    clearAllMarkers();

    const filteredActivities = getFilteredActivities();
    const currentZoom = map.getZoom();

    // Get current map bounds for viewport filtering
    const bounds = map.getBounds();
    if (!bounds) {
      console.log("Map bounds not available yet");
      return;
    }

    // Filter activities by viewport
    const activitiesInViewport = filteredActivities.filter((activity) => {
      if (!activity.coordinates || activity.coordinates.length === 0)
        return false;
      const position = activity.coordinates[0];
      return bounds.contains(
        new window.google.maps.LatLng(position.lat, position.lng)
      );
    });

    console.log(
      `${activitiesInViewport.length} activities in current viewport`
    );

    // For high-density areas, let clustering handle the performance
    // Only limit markers if there are a massive number AND no clustering
    let activitiesToShow = activitiesInViewport;

    if (!markerClusterer && activitiesInViewport.length > 500) {
      // Fallback limiting only when clustering is not available
      if (currentZoom < 6) {
        activitiesToShow = activitiesInViewport.filter(
          (_, index) => index % 5 === 0
        );
      } else if (currentZoom < 8) {
        activitiesToShow = activitiesInViewport.filter(
          (_, index) => index % 3 === 0
        );
      }
    }

    console.log(
      `Creating ${activitiesToShow.length} markers at zoom ${currentZoom}`
    );

    const markers = [];

    activitiesToShow.forEach((activity) => {
      if (!activity.coordinates || activity.coordinates.length === 0) return;

      const position = activity.coordinates[0];
      const color = getActivityColor(activity.type);

      try {
        const marker = new window.google.maps.Marker({
          position,
          title: activity.name,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            fillColor: color,
            fillOpacity: 0.9,
            strokeColor: activity.isRace ? "#FFD700" : "#FFFFFF",
            strokeWeight: activity.isRace ? 2 : 1,
            scale: 6,
          },
        });

        marker.addListener("click", () => {
          setSelectedActivity(activity);
          setShowingActivityRoute(false);
        });

        markers.push(marker);
      } catch (error) {
        console.error("Error creating marker:", error);
      }
    });

    // Add markers to clusterer or map
    if (markerClusterer) {
      markerClusterer.addMarkers(markers);
      console.log(`Added ${markers.length} markers to clusterer`);
    } else {
      // Fallback: add directly to map
      markers.forEach((marker) => {
        marker.setMap(map);
        markersRef.current.push(marker);
      });
      console.log(`Added ${markers.length} markers directly to map`);
    }

    const endTime = performance.now();
    console.log(
      `Marker update completed in ${(endTime - startTime).toFixed(2)}ms`
    );
  };

  const clearAllMarkers = () => {
    if (markerClusterer) {
      markerClusterer.clearMarkers();
    }

    markersRef.current.forEach((marker) => {
      if (marker && marker.setMap) {
        marker.setMap(null);
      }
    });
    markersRef.current = [];
  };

  const showActivityRoute = async (activity) => {
    if (!map || !athleteId) return;

    setDetailLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/activities/${activity.id}?athlete_id=${athleteId}`
      );

      if (!response.ok) {
        throw new Error("Failed to load activity route");
      }

      const detailedActivity = await response.json();

      // Clear existing polyline
      if (activityPolylineRef.current) {
        activityPolylineRef.current.setMap(null);
      }

      // Create new polyline
      const polyline = new window.google.maps.Polyline({
        path: detailedActivity.coordinates,
        geodesic: true,
        strokeColor: getActivityColor(activity.type),
        strokeOpacity: 0.8,
        strokeWeight: 4,
      });

      polyline.setMap(map);
      activityPolylineRef.current = polyline;

      // Fit bounds to route
      const bounds = new window.google.maps.LatLngBounds();
      detailedActivity.coordinates.forEach((coord) => bounds.extend(coord));
      map.fitBounds(bounds);

      setShowingActivityRoute(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const resetMapView = () => {
    if (!map) return;

    if (activityPolylineRef.current) {
      activityPolylineRef.current.setMap(null);
      activityPolylineRef.current = null;
    }

    map.setZoom(2);
    map.setCenter({ lat: 20, lng: 0 });
    setShowingActivityRoute(false);
    setSelectedActivity(null);

    // Force marker refresh after zoom reset
    setTimeout(() => {
      setShouldShowMarkers(true);
    }, 100);
  };

  // Loading Screen
  if (authLoading) {
    return (
      <div className="w-full h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-center">
          <Loader size={48} className="animate-spin mx-auto mb-4" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Authentication Screen
  if (!isAuthenticated) {
    return (
      <div className="w-full h-screen bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md text-center">
          <div className="mb-6">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              Strava World Map
            </h1>
            <p className="text-gray-600">
              Visualize all your Strava activities on an interactive world map
              and see which countries you've unlocked!
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              <strong>Error:</strong> {error}
            </div>
          )}

          <button
            onClick={login}
            className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-4 px-6 rounded-lg transition-colors flex items-center justify-center gap-3 text-lg"
          >
            <LogIn size={24} />
            Connect with Strava
          </button>

          <p className="text-sm text-gray-500 mt-4">
            You'll be redirected to Strava to authorize access to your
            activities
          </p>
        </div>
      </div>
    );
  }

  // Main App
  return (
    <div className="w-full h-screen relative bg-gray-900">
      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-20 bg-black/80 backdrop-blur-sm text-white">
        <div className="flex justify-between items-center p-4">
          <h1 className="text-2xl font-bold">My Strava World Map</h1>

          <div className="flex items-center gap-4 text-sm">
            <div className="hidden sm:flex items-center gap-4">
              <span>
                Countries:{" "}
                <strong className="text-green-400">
                  {getUnlockedCountries().length}
                </strong>
              </span>
              <span>
                Activities:{" "}
                <strong className="text-blue-400">{activities.length}</strong>
              </span>
              {!shouldShowMarkers && (
                <span className="text-yellow-400 text-xs">
                  Zoom in to see markers
                </span>
              )}
              {shouldShowMarkers && activities.length > 500 && (
                <span className="text-yellow-400 text-xs">
                  Showing subset at zoom {mapZoom}
                </span>
              )}
            </div>

            {(activitiesLoading || detailLoading) && (
              <Loader size={18} className="animate-spin text-blue-400" />
            )}

            {placeLookupLoading && (
              <span className="text-yellow-400 text-xs">
                Loading country data...
              </span>
            )}

            <div className="relative">
              <button
                onClick={() => setShowSyncOptions(!showSyncOptions)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
              >
                <RefreshCw size={18} />
                <span className="hidden sm:inline">Sync</span>
                <ChevronDown size={14} />
              </button>

              {showSyncOptions && (
                <div className="absolute top-full right-0 mt-1 bg-white rounded-lg shadow-lg border py-1 z-30 min-w-48">
                  <button
                    onClick={() => {
                      loadActivities(true, false); // Incremental sync
                      setShowSyncOptions(false);
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm text-gray-700"
                  >
                    <div className="font-medium">Quick Sync</div>
                    <div className="text-xs text-gray-500">
                      New activities only
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      loadActivities(true, true); // Full sync
                      setShowSyncOptions(false);
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm text-gray-700"
                  >
                    <div className="font-medium">Full Sync</div>
                    <div className="text-xs text-gray-500">
                      Re-download all (slow)
                    </div>
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
            >
              <Filter size={18} />
              <span className="hidden sm:inline">Filters</span>
            </button>

            {showingActivityRoute && (
              <button
                onClick={resetMapView}
                className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
              >
                <MapPin size={18} />
                <span className="hidden sm:inline">World View</span>
              </button>
            )}

            <button
              onClick={logout}
              className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
              title="Logout"
            >
              <User size={18} />
            </button>
          </div>
        </div>
      </header>

      {/* Error Banner */}
      {error && (
        <div className="absolute top-16 left-4 right-4 z-20 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg">
          <div className="flex justify-between items-center">
            <span>
              <strong>Error:</strong> {error}
            </span>
            <button
              onClick={() => setError(null)}
              className="text-red-500 hover:text-red-700"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <div className="absolute top-16 right-4 z-20 bg-white rounded-lg shadow-xl border p-4 w-64">
          <h3 className="font-semibold mb-3">Filter Activities</h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Activity Type
              </label>
              <select
                value={activityFilter}
                onChange={(e) => setActivityFilter(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="all">All Activities</option>
                <option value="run">🏃 Runs</option>
                <option value="ride">🚴 Rides</option>
                <option value="swim">🏊 Swims</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Race Filter
              </label>
              <select
                value={raceFilter}
                onChange={(e) => setRaceFilter(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="all">All Activities</option>
                <option value="race">🏆 Races Only</option>
                <option value="non-race">Regular Activities</option>
              </select>
            </div>
          </div>

          <div className="mt-4 pt-3 border-t text-sm text-gray-600">
            Showing <strong>{getFilteredActivities().length}</strong> of{" "}
            <strong>{activities.length}</strong> activities
          </div>
        </div>
      )}

      {/* Map Container */}
      <div ref={mapRef} className="w-full h-full" />

      {/* Activity Details Modal */}
      {selectedActivity && (
        <div className="absolute bottom-4 left-4 z-20 bg-white rounded-lg shadow-xl border max-w-sm">
          <div className="p-4">
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center gap-3">
                <span className="text-2xl">
                  {getActivityIcon(selectedActivity.type)}
                </span>
                <div>
                  <h3 className="font-bold text-lg leading-tight">
                    {selectedActivity.name}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {selectedActivity.country}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedActivity(null)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <X size={20} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">
                  Distance
                </div>
                <div className="font-semibold text-lg">
                  {selectedActivity.distance} km
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">
                  Time
                </div>
                <div className="font-semibold text-lg">
                  {selectedActivity.time}
                </div>
              </div>
            </div>

            {selectedActivity.isRace && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                <div className="flex items-center text-yellow-800">
                  <span className="font-semibold">🏆 Race Event</span>
                </div>
              </div>
            )}

            <button
              onClick={() => showActivityRoute(selectedActivity)}
              disabled={detailLoading}
              className="w-full btn-primary justify-center"
            >
              {detailLoading ? (
                <Loader size={18} className="animate-spin" />
              ) : (
                <Play size={18} />
              )}
              See Activity Route
            </button>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="absolute bottom-4 right-4 z-10 bg-white/90 backdrop-blur-sm rounded-lg p-3 text-sm shadow-lg">
        <h4 className="font-semibold mb-2">Legend</h4>
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <span>Runs</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span>Rides</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
            <span>Swims</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-gray-400 border-2 border-yellow-400"></div>
            <span>Races</span>
          </div>
        </div>
      </div>

      {/* Loading Overlay for No Activities */}
      {isAuthenticated &&
        activities.length === 0 &&
        !activitiesLoading &&
        !error && (
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-30">
            <div className="bg-white rounded-lg p-8 text-center max-w-md">
              <h3 className="text-xl font-bold mb-4">No Activities Found</h3>
              <p className="text-gray-600 mb-6">
                We couldn't find any activities in your Strava account. Make
                sure you have some runs, rides, or swims recorded!
              </p>
              <button
                onClick={() => loadActivities(true)}
                disabled={activitiesLoading}
                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium disabled:bg-blue-400"
                title="Sync with Strava"
              >
                <RefreshCw size={18} />
                <span className="hidden sm:inline">
                  {activitiesLoading ? "Syncing..." : "Sync"}
                </span>
              </button>
            </div>
          </div>
        )}

      {/* Google Maps Not Loaded */}
      {!mapLoaded && isAuthenticated && (
        <div className="absolute inset-0 bg-black/75 flex items-center justify-center z-30">
          <div className="bg-white rounded-lg p-6 text-center max-w-lg">
            <h3 className="text-xl font-bold mb-4">Setting up Google Maps</h3>
            <div className="text-left text-sm text-gray-600 space-y-2">
              <p>
                <strong>To complete setup:</strong>
              </p>
              <ol className="list-decimal list-inside space-y-1 ml-4">
                <li>Get a Google Maps API key</li>
                <li>Enable Maps JavaScript API</li>
                <li>Add REACT_APP_GOOGLE_MAPS_API_KEY to your .env file</li>
                <li>
                  For country highlighting: Create a Map ID with COUNTRY feature
                  layer
                </li>
              </ol>
            </div>
            <div className="mt-4 p-3 bg-blue-50 rounded">
              <p className="text-sm text-blue-800">
                <strong>Current API Key:</strong>{" "}
                {GOOGLE_MAPS_API_KEY.substring(0, 20)}...
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StravaWorldMap;
